import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { useAuth } from '../contexts/AuthContext';
import { Sparkles, CheckCircle2, X, ChevronRight, Gift, Rocket, Download, Smartphone, Monitor, Apple } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LAST_SEEN_VERSION_KEY = 'ecomtrack_last_seen_version';

// Platform detection utility
const detectPlatform = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Check for mobile devices first
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios';
  }
  
  // Check for desktop OS
  const platform = navigator.platform || '';
  if (platform.indexOf('Win') !== -1) {
    return 'windows';
  }
  if (platform.indexOf('Mac') !== -1) {
    return 'mac';
  }
  if (platform.indexOf('Linux') !== -1) {
    return 'linux';
  }
  
  return 'unknown';
};

// Platform icons and labels
const PLATFORM_CONFIG = {
  android: { icon: Smartphone, label: 'Android', color: 'from-green-500 to-green-600' },
  ios: { icon: Apple, label: 'iOS', color: 'from-gray-700 to-gray-900' },
  windows: { icon: Monitor, label: 'Windows', color: 'from-blue-500 to-blue-600' },
  mac: { icon: Apple, label: 'Mac', color: 'from-gray-700 to-gray-900' },
  linux: { icon: Monitor, label: 'Linux', color: 'from-orange-500 to-orange-600' },
  unknown: { icon: Download, label: 'Télécharger', color: 'from-green-500 to-emerald-600' }
};

export const UpdateAlertModal = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [update, setUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllPlatforms, setShowAllPlatforms] = useState(false);
  
  // Detect user's platform
  const detectedPlatform = useMemo(() => detectPlatform(), []);

  useEffect(() => {
    // Only check for updates if user is logged in
    if (user) {
      checkForUpdates();
    }
  }, [user]);

  const checkForUpdates = async () => {
    try {
      const lastSeenVersion = localStorage.getItem(LAST_SEEN_VERSION_KEY);
      
      const response = await axios.get(`${API}/updates/check`, {
        params: { 
          last_seen_version: lastSeenVersion,
          user_id: user?.id 
        }
      });

      if (response.data.has_update && response.data.update) {
        setUpdate(response.data.update);
        setOpen(true);
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    if (update?.version) {
      // Store locally for quick check
      localStorage.setItem(LAST_SEEN_VERSION_KEY, update.version);
      
      // Also persist on server so it works across devices
      if (user?.id) {
        try {
          await axios.post(`${API}/updates/dismiss`, null, {
            params: { 
              user_id: user.id, 
              version: update.version 
            }
          });
        } catch (error) {
          console.error('Error dismissing update:', error);
        }
      }
    }
    setOpen(false);
  };

  // Get download info for current platform
  const getDownloadInfo = () => {
    const platformDownloads = update?.platform_downloads;
    
    // First check multi-platform downloads
    if (platformDownloads) {
      const platformUrlKey = `${detectedPlatform}_url`;
      const platformLabelKey = `${detectedPlatform}_label`;
      
      if (platformDownloads[platformUrlKey]) {
        return {
          url: platformDownloads[platformUrlKey],
          label: platformDownloads[platformLabelKey] || `Télécharger pour ${PLATFORM_CONFIG[detectedPlatform]?.label || detectedPlatform}`,
          platform: detectedPlatform
        };
      }
      
      // Fallback to universal
      if (platformDownloads.universal_url) {
        return {
          url: platformDownloads.universal_url,
          label: platformDownloads.universal_label || 'Télécharger la mise à jour',
          platform: 'universal'
        };
      }
    }
    
    // Legacy single download URL
    if (update?.download_url) {
      return {
        url: update.download_url,
        label: update.download_label || 'Télécharger la mise à jour',
        platform: 'legacy'
      };
    }
    
    return null;
  };

  // Get all available platform downloads
  const getAllPlatformDownloads = () => {
    const platformDownloads = update?.platform_downloads;
    if (!platformDownloads) return [];
    
    const platforms = ['android', 'ios', 'windows', 'mac', 'linux'];
    const available = [];
    
    platforms.forEach(platform => {
      const urlKey = `${platform}_url`;
      const labelKey = `${platform}_label`;
      if (platformDownloads[urlKey]) {
        available.push({
          platform,
          url: platformDownloads[urlKey],
          label: platformDownloads[labelKey] || `Télécharger pour ${PLATFORM_CONFIG[platform]?.label}`,
          config: PLATFORM_CONFIG[platform]
        });
      }
    });
    
    // Add universal if exists
    if (platformDownloads.universal_url) {
      available.push({
        platform: 'universal',
        url: platformDownloads.universal_url,
        label: platformDownloads.universal_label || 'Télécharger (Universel)',
        config: PLATFORM_CONFIG.unknown
      });
    }
    
    return available;
  };

  const handleDownload = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleViewHistory = () => {
    handleDismiss();
    // Navigate to settings or updates page if needed
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading || !update || !user) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="update-alert-modal" aria-describedby="update-description">
        {/* Header with gradient */}
        <div className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 p-6 text-white">
          <DialogHeader className="text-white">
            <DialogTitle className="sr-only">Nouvelle mise à jour disponible</DialogTitle>
          </DialogHeader>
          <div className="absolute top-4 right-4">
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-white/80 hover:text-white hover:bg-white/20"
              onClick={handleDismiss}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              {update.is_major ? (
                <Rocket className="h-8 w-8" />
              ) : (
                <Sparkles className="h-8 w-8" />
              )}
            </div>
            <div>
              <Badge className="bg-white/20 text-white border-0 mb-1">
                Version {update.version}
              </Badge>
              <h2 className="text-xl font-bold">{update.title}</h2>
            </div>
          </div>
          
          {update.description && (
            <p id="update-description" className="text-white/90 text-sm">{update.description}</p>
          )}
          
          <p className="text-white/60 text-xs mt-3">
            {formatDate(update.release_date)}
          </p>
        </div>

        {/* Features list */}
        <div className="p-6">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Gift className="h-4 w-4" />
            Nouveautés
          </h3>
          
          <ScrollArea className="max-h-[250px]">
            <div className="space-y-3">
              {update.features?.map((feature, idx) => (
                <div 
                  key={idx}
                  className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center text-xl">
                    {feature.icon || '✨'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-slate-800">{feature.title}</h4>
                    <p className="text-sm text-slate-500 mt-0.5">{feature.description}</p>
                  </div>
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 space-y-3">
          {/* Platform-specific download buttons */}
          {(() => {
            const downloadInfo = getDownloadInfo();
            const allPlatforms = getAllPlatformDownloads();
            const hasMultiplePlatforms = allPlatforms.length > 1;
            
            if (!downloadInfo && allPlatforms.length === 0) return null;
            
            const PlatformIcon = downloadInfo ? (PLATFORM_CONFIG[downloadInfo.platform]?.icon || Download) : Download;
            const platformColor = downloadInfo ? (PLATFORM_CONFIG[downloadInfo.platform]?.color || 'from-green-500 to-emerald-600') : 'from-green-500 to-emerald-600';
            
            return (
              <div className="space-y-2">
                {/* Primary download button for detected platform */}
                {downloadInfo && (
                  <Button 
                    className={`w-full bg-gradient-to-r ${platformColor} hover:opacity-90 text-white`}
                    onClick={() => handleDownload(downloadInfo.url)}
                    data-testid="update-download-btn"
                  >
                    <PlatformIcon className="h-4 w-4 mr-2" />
                    {downloadInfo.label}
                  </Button>
                )}
                
                {/* Toggle for other platforms */}
                {hasMultiplePlatforms && (
                  <>
                    <button
                      onClick={() => setShowAllPlatforms(!showAllPlatforms)}
                      className="w-full text-center text-sm text-slate-500 hover:text-slate-700 py-1"
                    >
                      {showAllPlatforms ? '▲ Masquer les autres plateformes' : '▼ Voir les autres plateformes'}
                    </button>
                    
                    {showAllPlatforms && (
                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-200">
                        {allPlatforms
                          .filter(p => p.platform !== downloadInfo?.platform)
                          .map((platform) => {
                            const Icon = platform.config.icon;
                            return (
                              <Button
                                key={platform.platform}
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-2"
                                onClick={() => handleDownload(platform.url)}
                                data-testid={`download-${platform.platform}-btn`}
                              >
                                <Icon className="h-4 w-4" />
                                <span className="text-xs">{platform.config.label}</span>
                              </Button>
                            );
                          })}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}
          
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              className="text-slate-500"
              onClick={handleViewHistory}
            >
              Voir l'historique
            </Button>
            <Button 
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              onClick={handleDismiss}
              data-testid="update-dismiss-btn"
            >
              C'est compris
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateAlertModal;
